import React from 'react';
import withLayout from 'hocs/withLayout';
import { PageProps } from 'types/page';
import { useQuery } from '@apollo/client/react';
import Staff from './Staff';
import { STAFF } from 'lib/graphql/queries/user';
import { Seo, StaffUsers } from '@elevator/ui';
import { DOMAIN_BASE_SHORT, ROUTES } from 'config/Nav';

/**
 * Staff Layout Wrapper
 *
 * Handles client-side data fetching for the staff page.
 * Uses cache-first fetchPolicy to avoid duplicate fetches and improve performance.
 *
 * Features:
 * - Cache-first fetchPolicy (avoids duplicate fetches)
 * - Loading state management
 * - Error handling
 * - SEO metadata
 *
 * Used in:
 * - /staff page
 *
 * @returns Staff layout with data management
 */
const StaffIndex = () => {
  const { data, loading, networkStatus, error, fetchMore } = useQuery<any>(STAFF, {
    fetchPolicy: 'cache-first', // Use cache-first to avoid unnecessary re-fetch
    nextFetchPolicy: 'cache-first', // Keep using cache for subsequent fetches
    notifyOnNetworkStatusChange: true,
  });

  const staff = data && data.staffUsers ? data.staffUsers : null;
  const isLoading = (loading && networkStatus !== 3) || networkStatus === 4;
  const loadingMore = networkStatus === 3;

  return (
    <>
      <Seo
        type={'website'}
        data={{
          baseUrl: DOMAIN_BASE_SHORT,
          title: 'Our Staff',
          description: 'Meet the ELEVATOR team.',
          path: ROUTES.STAFF.to,
        }}
      />
      <Staff
        staffUsers={staff}
        loading={isLoading}
        loadingMore={loadingMore}
        error={error}
        fetchMore={fetchMore}
      />
    </>
  );
};

export interface StaffIndexTypes extends PageProps {}

/** `md` content column (940px max), centered via `withLayout`. */
export default withLayout(StaffIndex, { contentWidth: 'md', margin: true });
